import { asText } from '@prismicio/helpers';
import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMarkBase from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { ButtonInline, Button } from '@remote-com/norma/core/button';
import { useRouter } from 'next/router';
import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import { Modal } from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  ModalDescription,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
} from 'remote-com-shared/components/ModalForm/styles';
import {
  RenderFieldText,
  universalFieldHasContent,
} from 'remote-com-shared/domains/contentful/prismicBridge';
import { getFields, handleSpecificHubSpotFields } from 'remote-com-shared/domains/hubspot/fields';
import { handleFormRedirect } from 'remote-com-shared/domains/hubspot/helpers';
import { useHubspotForm } from 'remote-com-shared/domains/hubspot/hooks';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { LocalisedContentConsumer } from 'remote-com-shared/domains/localisedContent/context';
import styled, { ThemeProvider } from 'styled-components';

const PrimaryButton = styled(Button).attrs({
  tone: 'primary',
  shadow: true,
})``;

const SuccessMark = styled(SuccessMarkBase)`
  svg {
    --circle-check-clr: var(--colors-blank);
    --circle-bg: ${({ theme }) => theme.colors.irisBlue_30};
    --circle-border-clr: var(--colors-irisBlue);

    > g > circle {
      fill-opacity: 0.8;
    }
  }
`;

function Form({
  onCancel,
  onSuccess,
  formId,
  formTitle: formTitleRaw,
  formName = 'hubspot-form',
  formSubhead,
  formTheme = 'dark',
  formSize = 'small',
  promoCode,
  successActions,
  gatedFile,
  gatedFileDownloadLabel,
  // Any values (e.g. in hidden fields) that need to be added while submitting the form
  onSubmitAppendValues = {},
}) {
  // just in case Prismic uses Title or Rich Text for the modal title
  const formTitle = typeof formTitleRaw === 'string' ? formTitleRaw : asText(formTitleRaw);

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const {
    isLoading,
    submitText = '',
    inlineMessage: successMessage = '',
    formFieldGroups = [],
    submitUrl,
    hasRedirect,
    redirect,
  } = useHubspotForm(formId);

  const fields = getFields(formFieldGroups).map((field) =>
    handleSpecificHubSpotFields({ ...field })
  );

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({ promo_code: promoCode });

  const reCaptchaRef = useRef(null);

  async function handleSubmit(values, { setSubmitting }) {
    const updatedFormValues = {
      ...formValues,
      ...values,
      ...onSubmitAppendValues,
    };

    setFormValues(updatedFormValues);

    setShowLoader(true);

    if (isCaptchaDone) {
      try {
        await registerLead(
          {
            ...values,
            ...onSubmitAppendValues,
            ...(Object.prototype.hasOwnProperty.call(values, 'utm')
              ? {
                  utm: getCurrentURL(),
                }
              : {}),
          },
          submitUrl
        );

        dataLayerPush({ event: 'user-submitted-hubspot-form', label: window.location.pathname });

        if (onSuccess) {
          onSuccess();
        }

        setShowLoader(false);
        setShowSuccessState(true);
        if (hasRedirect) {
          handleFormRedirect(redirect);
        }
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);
      }
    }
  }

  const handleCaptchaChange = (value) => {
    setIsCaptchaDone(value);
  };

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />
      <FormContainer>
        {showSuccessState ? (
          <SubsribeSuccessContainer>
            <SuccessMark />
            <SuccessTitle>
              <LocalisedContentConsumer>
                {({ formSuccessThankYou }) => formSuccessThankYou}
              </LocalisedContentConsumer>
            </SuccessTitle>
            <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            {onCancel && (
              <SuccessActions>
                {successActions || <SubmitButton onClick={() => onCancel()}>Close</SubmitButton>}
                {gatedFile && (
                  <Button href={gatedFile.url} target={gatedFile.target}>
                    {gatedFileDownloadLabel || 'Download'}
                  </Button>
                )}
              </SuccessActions>
            )}
          </SubsribeSuccessContainer>
        ) : (
          <DynamicFormWrapper>
            {formTitle && <ModalTitle>{formTitle}</ModalTitle>}
            {universalFieldHasContent(formSubhead) && (
              <ModalDescription as="div">
                <RenderFieldText field={formSubhead} />
              </ModalDescription>
            )}
            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            {isLoading ? (
              <SubmitButtonWrapper>
                <SubmitButton isLoading={isLoading}>Loading</SubmitButton>
              </SubmitButtonWrapper>
            ) : (
              <>
                <DynamicForm
                  formName={formName}
                  fields={fields}
                  fieldValues={formValues}
                  formError={formState.errorMessage}
                  formTheme={formTheme}
                  formSize={formSize}
                  handleOnSubmit={handleSubmit}
                  isLoading={formState.isLoading}
                  enableReinitialize
                />

                <CaptchaWrapper>
                  <LocaleContextConsumer>
                    {(locale) => (
                      <ReCAPTCHA
                        hl={locale}
                        ref={reCaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                        onChange={handleCaptchaChange}
                      />
                    )}
                  </LocaleContextConsumer>
                </CaptchaWrapper>
                <SubmitButtonWrapper>
                  <SubmitButton isLoading={showLoader} disabled={!isCaptchaDone} form={formName}>
                    {submitText}
                  </SubmitButton>
                </SubmitButtonWrapper>
              </>
            )}
          </DynamicFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
}

export const HubspotFormModalButton = ({
  children,
  formId,
  startOpen,
  startOpenViaUrl,
  formName,
  ...props
}) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (startOpen) {
      setShowModal(true);
    }

    if (startOpenViaUrl) {
      // If formName exists as url parameter with a truthy value
      setShowModal(Boolean(router.query[formName]));
    }
  }, [startOpen, router, formName, startOpenViaUrl]);

  function openModal() {
    setShowModal(true);
  }
  function closeModal() {
    setShowModal(false);
  }
  const { as: buttonAlias, ...buttonProps } = props.button || {};
  const CTAComponent = buttonAlias || props.as || PrimaryButton;

  return (
    <>
      <CTAComponent onClick={openModal} {...(buttonProps || {})}>
        {children}
      </CTAComponent>
      <HubspotFormModal
        visible={showModal}
        onCancel={closeModal}
        formId={formId}
        footer={null}
        {...props}
      />
    </>
  );
};

export const HubspotForm = ({ maxWidth, formId, ...props }) => {
  if (!formId) {
    return null;
  }
  return <Form {...{ formId }} {...props} successActions />;
};

export const HubspotFormModal = ({ visible, onCancel, formId, ...props }) => {
  if (!formId) {
    return null;
  }
  return (
    <Modal {...{ visible, onCancel }} footer={null} {...props}>
      <Form {...{ onCancel, formId }} {...props} />
    </Modal>
  );
};
